import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { API_BASE, getImageUrl } from '../utils/api';

interface Podcast {
    _id: string;
    title: string;
    description: string;
    videoUrl: string;
    thumbnail: string;
}

// Desktop arrows only
const NextArrow = ({ onClick }: any) => (
    <button
        onClick={onClick}
        className="hidden md:flex absolute -top-12 right-0 w-10 h-10 bg-[#05144b] text-white rounded-xl items-center justify-center shadow hover:bg-[#f1ae24] hover:text-[#05144b] transition-all"
    >
        <i className="fa-solid fa-chevron-right text-sm"></i>
    </button>
);

const PrevArrow = ({ onClick }: any) => (
    <button
        onClick={onClick}
        className="hidden md:flex absolute -top-12 right-12 w-10 h-10 bg-[#05144b] text-white rounded-xl items-center justify-center shadow hover:bg-[#f1ae24] hover:text-[#05144b] transition-all"
    >
        <i className="fa-solid fa-chevron-left text-sm"></i>
    </button>
);

export default function PodcastSection() {

    const [podcasts, setPodcasts] = useState<Podcast[]>([]);
    const [loading, setLoading] = useState(true);
    const [playingId, setPlayingId] = useState<string | null>(null);

    useEffect(() => {
        fetch(`${API_BASE}/podcasts`)
            .then(r => r.json())
            .then(data => Array.isArray(data) && setPodcasts(data))
            .finally(() => setLoading(false));
    }, []);

    const getEmbedUrl = (url: string) => {
        if (url.includes('watch?v=')) return url.replace('watch?v=', 'embed/');
        if (url.includes('youtu.be/')) return url.replace('youtu.be/', 'youtube.com/embed/');
        return url;
    };

    const settings = {
        dots: true,
        infinite: podcasts.length > 1,
        speed: 500,
        slidesToShow: 2,
        autoplay: true,
        autoplaySpeed: 4000,
        arrows: true,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
        responsive: [
            {
                breakpoint: 1024,
                settings: { slidesToShow: 1.5 }
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 1.2,
                    arrows: false,
                    centerMode: true,
                    centerPadding: '12px'
                }
            },
            {
                breakpoint: 480,
                settings: {
                    slidesToShow: 1,
                    arrows: false,
                    centerMode: true,
                    centerPadding: '0px',
                    infinite: podcasts.length > 1
                }
            }
        ]
    };

    if (loading || podcasts.length === 0) return null;

    return (
        <div className="max-w-[1400px] mx-auto px-4">

                {/* SLIDER */}
                <div className="relative">

                    {/* spacing fix */}
                    <style>
                        {`
                        .slick-slide > div {
                            margin: 0 8px;
                        }
                        .slick-list {
                            padding-left: 10px;
                            padding-right: 10px;
                        }
                        .slick-dots {
                            bottom: -10px !important;
                        }
                        .slick-dots li button:before {
                            font-size: 8px !important;
                            color: #05144b !important;
                        }
                        .slick-dots li.slick-active button:before {
                            color: #f1ae24 !important;
                        }
                        `}
                    </style>

                    <Slider {...settings}>

                        {podcasts.map((podcast) => (

                            <div key={podcast._id} className="pb-4">

                                <div className="bg-white rounded-2xl md:rounded-[2rem] overflow-hidden shadow-md hover:shadow-2xl transition duration-300">

                                    {/* VIDEO / THUMB */}
                                    <div
                                        className="relative h-[180px] md:h-[240px] cursor-pointer"
                                        onClick={() => setPlayingId(podcast._id)}
                                    >
                                        {playingId === podcast._id ? (
                                            <iframe
                                                src={`${getEmbedUrl(podcast.videoUrl)}?autoplay=1`}
                                                className="w-full h-full"
                                                allowFullScreen
                                            />
                                        ) : (
                                            <>
                                                <img
                                                    src={getImageUrl(podcast.thumbnail)}
                                                    className="w-full h-full object-cover"
                                                />

                                                {/* overlay */}
                                                <div className="absolute inset-0 bg-black/40"></div>

                                                {/* play button */}
                                                <div className="absolute inset-0 flex items-center justify-center">
                                                    <div className="w-14 h-14 md:w-16 md:h-16 bg-white/20 backdrop-blur rounded-full flex items-center justify-center">
                                                        <i className="fa-solid fa-play text-white text-xl ml-1"></i>
                                                    </div>
                                                </div>

                                                {/* badge */}
                                                <div className="absolute top-3 right-3 bg-[#f1ae24] text-[#05144b] text-[10px] px-2 py-1 rounded font-black uppercase tracking-wider">
                                                    NEW
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {/* CONTENT */}
                                    <div className="p-4 md:p-5">

                                        <h3 className="text-sm md:text-lg font-bold text-black line-clamp-1">
                                            {podcast.title}
                                        </h3>

                                        <p className="text-black text-xs mt-2 line-clamp-2">
                                            {podcast.description || "Latest property insights & expert talk"}
                                        </p>

                                        {/* footer */}
                                        <div className="flex items-center justify-between mt-4">

                                            <span className="text-[10px] text-black font-semibold">
                                                ▶ Watch Now
                                            </span>

                                            <button
                                                onClick={() => setPlayingId(podcast._id)}
                                                className="bg-[#f1ae24] px-3 py-1 text-[11px] font-black text-[#05144b] rounded-lg uppercase tracking-wider hover:brightness-105 active:scale-95 transition-all"
                                            >
                                                PLAY
                                            </button>

                                        </div>

                                    </div>

                                </div>

                            </div>
                        ))}

                    </Slider>
                </div>

        </div>
    );
}